<template>
  <div class="mx-auto my-2">
    <div v-if="!showTable" class="import-entry-shell">
      <v-card class="import-entry-card" flat>
        <div class="import-entry-card__header">
          <div>
            <div class="import-entry-card__eyebrow">Import Helper</div>
            <div class="import-entry-card__title">Generic Entry Import</div>
            <div class="import-entry-card__subtitle">
              Download the current template first, then upload the completed
              file and review the validation summary before import.
            </div>
          </div>
          <div class="import-entry-card__actions">
            <v-btn
 v-if="$can('generic-entry', 'downloadTemplate')"              :disabled="lookupLoading"
              color="primary"
              depressed
              rounded
              @click="downloadGenericTemplate"
            >
              <v-icon left>mdi-file-download-outline</v-icon>
              <span>Download Template</span>
            </v-btn>
            <v-btn color="primary" outlined rounded @click="goBack()">
              <v-icon left>mdi-arrow-left</v-icon>
              <span>Back to Records</span>
            </v-btn>
          </div>
        </div>
        <div class="import-entry-card__steps">
          <div class="import-entry-card__step">
            <span class="import-entry-card__step-number">1</span>
            <span>Download the current generic entry template with active lookup values.</span>
          </div>
          <div class="import-entry-card__step">
            <span class="import-entry-card__step-number">2</span>
            <span>Fill the spreadsheet without renaming or removing the headers.</span>
          </div>
          <div class="import-entry-card__step">
            <span class="import-entry-card__step-number">3</span>
            <span>Upload the completed file and review the validation summary before import.</span>
          </div>
        </div>

        <div v-if="!showUpload" class="import-entry-card__uploader">
          <upload-excel-component
              :before-upload="beforeUpload"
              :on-success="handleSuccess"
          />
        </div>
      </v-card>
    </div>

    <graduate-import-review
        v-if="showUpload"
        :file-name="fileName"
        :loading="loading"
        :row-count="tableData.length"
        action-label="Import Generic Entry Rows"
        title="Generic Entry spreadsheet validated"
        @cancel="goBack"
        @replace="replaceImportFile"
        @upload="uploadGraduates"
    />

    <v-card v-if="showTable" class="mx-2 preservice-list-card" flat>
      <div class="filter-toolbar">
        <div class="filter-toolbar__header">
          <div>
            <div class="filter-toolbar__eyebrow">Pre-service Filters</div>
            <div class="filter-toolbar__title">Generic Entry</div>
          </div>
          <div class="filter-toolbar__actions">
            <v-btn
                class="filter-toolbar__clear-button"
                color="primary"
                outlined
                rounded
                @click="clearFilters"
            >
              <v-icon left>mdi-filter-remove-outline</v-icon>
              <span>Clear Filters</span>
            </v-btn>
            <v-btn
                v-if="$can('generic-entry', 'import', (canCreateGEntry))"
                class="filter-toolbar__secondary-button"
                color="primary"
                depressed
                rounded
                @click="newImport"
            >
              <v-icon left>mdi-import</v-icon>
              <span>New Import</span>
            </v-btn>
            <v-btn
                v-if="canCreateGEntry"
                class="filter-toolbar__primary-button"
                color="success"
                depressed
                rounded
                @click="addGenericEntry"
            >
              <v-icon left>mdi-plus-circle</v-icon>
              <span>Add</span>
            </v-btn>
          </div>
        </div>
        <v-row class="filter-toolbar__fields" dense>
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-text-field
                v-model="name"
                class="filter-toolbar__field"
                dense
                hide-details
                label="Search by Name"
                outlined
                prepend-inner-icon="mdi-account-search-outline"
                rounded
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-text-field
                v-model="idValue"
                class="filter-toolbar__field"
                dense
                hide-details
                label="National ID (FAN)"
                outlined
                prepend-inner-icon="mdi-card-account-details-outline"
                rounded
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-autocomplete
                v-model="programValue"
                :clearable="clearable"
                :items="EDUCATIONAL_PROGRAMS"
                :loading="lookupLoading"
                class="filter-toolbar__field"
                dense
                item-text="name"
                item-value="id"
                label="Program Education Type"
                outlined
                prepend-inner-icon="mdi-shape-outline"
                return-object
                rounded
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="6" lg="2">
            <v-autocomplete
                v-model="disciplineValue"
                :clearable="clearable"
                :items="DISCIPLINES"
                :loading="lookupLoading"
                class="filter-toolbar__field"
                dense
                item-text="name"
                item-value="id"
                label="Department / Discipline"
                outlined
                prepend-inner-icon="mdi-stethoscope"
                return-object
                rounded
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="6" lg="2">
            <v-autocomplete
                v-model="levelOfStudyValue"
                :clearable="clearable"
                :items="LEVEL_OF_STUDIES"
                :loading="lookupLoading"
                class="filter-toolbar__field"
                dense
                item-text="name"
                item-value="id"
                label="Level Of Study"
                outlined
                prepend-inner-icon="mdi-school-outline"
                return-object
                rounded
            ></v-autocomplete>
          </v-col>
        </v-row>
      </div>
      <v-data-table
          :headers="headers"
          :items="tableData.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="tableData.count"
          class="elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.fullName`]="{ item }">
          {{ item.fullName ? item.fullName : "-" }}
        </template>
        <template v-slot:[`item.sex`]="{ item }">
          {{ item.sex ? item.sex : "-" }}
        </template>
        <template v-slot:[`item.idNo`]="{ item }">
          {{ item.idNo ? formatFanId(item.idNo) : "-" }}
        </template>
        <template v-slot:[`item.educationProgram`]="{ item }">
          {{ item.educationProgram ? item.educationProgram : "-" }}
        </template>
        <template v-slot:[`item.entryYear`]="{ item }">
          {{ item.entryYear ? item.entryYear : "-" }}
        </template>
        <template v-slot:[`item.yearOfGraduation`]="{ item }">
          {{ item.yearOfGraduation ? item.yearOfGraduation : "-" }}
        </template>
        <template v-slot:[`item.higherLearningInstitution`]="{ item }">
          {{
            item.higherLearningInstitution
                ? item.higherLearningInstitution
                : "-"
          }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                  class="preservice-record-action preservice-record-action--view mr-2"
                  @click="viewImportedGraduates(item)"
                  v-on="on"
              >mdi-eye
              </v-icon
              >
            </template>
            <span>View generic entry pre-service detail</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-if="canEditGEntry" v-slot:activator="{ on }">
              <v-icon
                  class="preservice-record-action preservice-record-action--edit mr-2"
                  @click="editImportedGraduate(item)"
                  v-on="on"
              >mdi-circle-edit-outline
              </v-icon
              >
            </template>
            <span>Edit Generic Entries</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="detailDialog" persistent width="60%">
        <v-card class="preservice-dialog-card">
          <v-card-title class="preservice-dialog-header">
            <div class="preservice-dialog-header__icon">
              <v-icon color="white">mdi-account-details-outline</v-icon>
            </div>
            <div class="preservice-dialog-header__content">
              <div class="preservice-dialog-header__title">{{ title }}</div>
              <div class="preservice-dialog-header__subtitle">
                Review identity, education, institution, and grade information.
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn
                aria-label="Close student detail dialog"
                class="preservice-dialog-close"
                icon
                @click="closeDialog()"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <div class="preservice-dialog-body">
            <GraduatesDetail
                v-if="detailDialog"
                :graduates="graduates"
                @close="detailDialog = false"
            ></GraduatesDetail>
          </div>
        </v-card>
      </v-dialog>
    </template>

    <template>
      <v-dialog v-model="addDialog" persistent width="50%">
        <v-card class="preservice-dialog-card">
          <v-card-title class="preservice-dialog-header">
            <div class="preservice-dialog-header__icon">
              <v-icon color="white">
                {{ graduates ? "mdi-account-edit-outline" : "mdi-account-plus-outline" }}
              </v-icon>
            </div>
            <div class="preservice-dialog-header__content">
              <div class="preservice-dialog-header__title">{{ title }}</div>
              <div class="preservice-dialog-header__subtitle">
                {{
                  graduates
                    ? "Update this student's pre-service record."
                    : "Create a new pre-service student record."
                }}
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn
                aria-label="Close create or edit student dialog"
                class="preservice-dialog-close"
                icon
                @click="closeDialog()"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <div class="preservice-dialog-body">
            <HrdAddGenericEntry
                v-if="addDialog"
                :graduates="graduates"
                :submitting="saving"
                @close="addDialog = false"
                @registerOrUpdateGenericEntries="registerOrUpdateGenericEntries"
            ></HrdAddGenericEntry>
          </div>
        </v-card>
      </v-dialog>
    </template>
    <importErrorModal
        :dialogState="importErrorsDialog"
        :importErrors="importErrors"
        @dialogEvent="() =>importErrorsDialog = false"
    ></importErrorModal>
  </div>
</template>

<script>
import UploadExcelComponent from "../UploadExcel.vue";
import {GraduateService} from "../graduates.service";
import GraduatesDetail from "./graduates-detail.vue";
// import UpdateImportedGraduate from "./update-imported-graduate.vue";
import HrdAddGenericEntry from "./add-generic-entry.vue";
import {
  getDisciplines,
  getEducationalPrograms,
  getEthnicities,
  getLevelOfStudies,
  getRegionList,
  getStudentStatuses,
  getUser,
  getUniversities,
  getWoredaList,
  getZoneList,
  getAssignedUniversityOption,
  isUniversityScopedPreserviceUser,
} from "@/utils/helper";
import importErrorModal from "../importErrorModal.vue";
import {EducLevelEnum, YearOfEducEnum} from "@/utils/enums";
import GraduateImportReview from "@/components/Graduates/GraduateImportReview.vue";
import {
  buildGenericEntryTemplateOptions,
  formatAllowedValues,
  GENERIC_ENTRY_TEMPLATE_HEADERS,
  LEGACY_GENERIC_ENTRY_TEMPLATE_HEADERS,
  PRESERVICE_STATUS_SHEET_NAME,
  validateGenericEntryImport,
  validatePreserviceImportSource,
} from "@/components/Graduates/graduate-import-utils";
import {downloadStructuredTemplateWorkbook} from "@/components/Graduates/structured-template-workbook";
import {formatFanId, normalizeFanId} from "@/utils/fan-id";
const ExcelJS = require("exceljs");
const {saveAs} = require("file-saver");

export default {
  components: {
    UploadExcelComponent,
    GraduatesDetail,
    // UpdateImportedGraduate,
    HrdAddGenericEntry,
    importErrorModal,
    GraduateImportReview,
  },
  data() {
    return {
      tableData: [],
      tableHeader: [],
      fileName: "",
      importErrorsDialog: false,
      importErrors: [],
      loading: false,
      saving: false,
      showTable: true,
      showUpload: false,
      showUploadExcel: false,
      headers: [
        {text: "Full Name", value: "fullName"},
        {text: "Sex", value: "sex"},
        // { text: "Date Of Birth", value: "dateOfBirth" },
        {text: "National ID (FAN)", value: "idNo"},
        // { text: "Department / Discipline", value: "profession" },
        {text: "Program Education Type", value: "educationProgram.name"},
        // { text: "Level Of Study", value: "levelOfStudy" },
        // { text: "Entry Program", value: "entryProgram" },
        {text: "Year of Entry", value: "entryYear"},
        // { text: "Proposed Year Of Graduation", value: "proposedYearOfGraduation" },
        // { text: "Year Of Graduation", value: "yearOfGraduation" },
        // { text: "Year Of Education", value: "yearOfEducation" },
        {text: "Year Of Graduation", value: "graduationYear"},
        // { text: "University Owner", value: "universityOwner" },
        {
          text: "Higher Learning Institution",
          value: "university.name",
        },
        // { text: "Region", value: "region" },
        // { text: "Town", value: "town" },
        // { text: "GPA", value: "GPA" },
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      graduates: [],
      detailDialog: false,
      dialog: false,
      title: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      addDialog: false,

      lookupLoading: false,
      LEVEL_OF_STUDIES: [],
      DISCIPLINES: [],
      EDUCATIONAL_PROGRAMS: [],
      EDUC_LEVEL: [],
      STUDENT_STATUSES: [],
      YEAR_OF_EDUCATION: [],
      ETHNICITIES: [],
      BIRTH_REGIONS: [],
      BIRTH_ZONES: [],
      BIRTH_WOREDAS: [],
      UNIVERSITIES: [],

      programValue: null,
      allDisciplines: [],
      disciplineValue: null,
      levelOfStudyValue: null,
      idValue: null,
      name: null,
      clearable: true,
    };
  },
  async beforeMount() {
    this.fetchImportedGraduates();
    await this.loadLookups();
  },
  watch: {
    options: {
      handler() {
        this.fetchImportedGraduates();
      },
      deep: true,
    },
    name: function (val) {
      this.name = val;
      this.fetchImportedGraduates();
    },
    idValue: function (val) {
      this.idValue = formatFanId(val);
      this.fetchImportedGraduates();
    },
    programValue: function (val) {
      this.programValue = val == '' ? null : val;
      this.fetchOnFilters();
      this.fetchImportedGraduates();
    },
    disciplineValue: function (val) {
      this.disciplineValue = val == '' ? null : val;
      this.fetchImportedGraduates();
    },
    levelOfStudyValue: function (val) {
      this.levelOfStudyValue = val == '' ? null : val;
      this.fetchImportedGraduates();
    },

  },
  computed: {
    canCreateGEntry() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Pre-service" &&
              permission.canCreate
      );
    },
    canEditGEntry() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Pre-service" &&
              permission.canUpdate
      );
    },
  },
  methods: {
    formatFanId,
    buildGenericEntryWorkbookTemplateOptions() {
      return buildGenericEntryTemplateOptions({
        lookups: {
          disciplines: this.allDisciplines,
          educationalPrograms: this.EDUCATIONAL_PROGRAMS,
          ethnicities: this.ETHNICITIES,
          birthRegions: this.BIRTH_REGIONS,
          birthZones: this.BIRTH_ZONES,
          birthWoredas: this.BIRTH_WOREDAS,
          levelOfStudies: this.LEVEL_OF_STUDIES,
          studentStatuses: this.STUDENT_STATUSES,
          yearOfEducation: this.YEAR_OF_EDUCATION,
          universities: this.UNIVERSITIES,
        },
      });
    },
    async downloadGenericTemplate() {
      const templateOptions = this.buildGenericEntryWorkbookTemplateOptions();
      if (
        !Object.values(templateOptions.missingRequirements || {}).every(Boolean)
      ) {
        this.$toast.error(
          "Unable to generate the Generic Entry template because one or more lookups are missing.",
        );
        return;
      }

      await downloadStructuredTemplateWorkbook({
        categoryLabel: "Generic Entry",
        fileName: "Generic_Entry_Template.xlsx",
        requiresGpa: true,
        templateOptions,
      });
      this.$toast.success("Generic_Entry_Template.xlsx downloaded.");
    },
    getColumnLetter(index) {
      let dividend = index;
      let columnName = "";
      while (dividend > 0) {
        const modulo = (dividend - 1) % 26;
        columnName = String.fromCharCode(65 + modulo) + columnName;
        dividend = Math.floor((dividend - modulo) / 26);
      }
      return columnName;
    },
    async downloadGenericTemplateLegacy() {
      const templateOptions = this.buildGenericEntryWorkbookTemplateOptions();
      if (
        !Object.values(templateOptions.missingRequirements || {}).every(Boolean)
      ) {
        this.$toast.error(
          "Unable to generate the Generic Entry template because one or more lookups are missing.",
        );
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const templateSheet = workbook.addWorksheet("Generic Entry Template");
      const instructionSheet = workbook.addWorksheet("Instructions");
      const lookupSheet = workbook.addWorksheet("Lookups");
      lookupSheet.state = "veryHidden";
      const currentYear = new Date().getFullYear();
      const maxBirthDate = `${currentYear - 17}-12-31`;
      const columnMap = templateOptions.headers.reduce(
        (accumulator, header, index) => {
          accumulator[header] = this.getColumnLetter(index + 1);
          return accumulator;
        },
        {},
      );
      const lastColumnLetter = this.getColumnLetter(
        templateOptions.headers.length,
      );

      instructionSheet.addRow(["Generic Entry import template"]);
      instructionSheet.addRow([
        "1. Use the 'Generic Entry Template' sheet only.",
      ]);
      instructionSheet.addRow([
        "2. Use dropdown columns for Sex, Ethnicity, Birth Region, Birth Zone, Birth Woreda, Program Education Type, Department / Discipline, Level Of Study, Year of Education, and Student Status.",
      ]);
      instructionSheet.addRow([
        `3. Date of Birth must be on or before ${maxBirthDate} and entered as YYYY-MM-DD or DD-MM-YYYY.`,
      ]);
      instructionSheet.addRow([
        `4. Year of Entry must be between 1950 and ${currentYear}.`,
      ]);
      instructionSheet.addRow([
        "5. Expected Graduation Year is required. Year of Graduation is optional, but if provided it cannot be earlier than Expected Graduation Year.",
      ]);
      instructionSheet.addRow([
        "6. National ID (FAN) must contain exactly 16 digits and should be entered in a Text cell.",
      ]);
      instructionSheet.addRow([
        "7. Birth Zone and Birth Woreda must match the selected Birth Region and Birth Zone.",
      ]);
      instructionSheet.addRow([
        "8. GPA and Cumulative GPA must be between 0 and 4.",
      ]);
      instructionSheet.addRow([
        `9. Leave Nationality blank to use the default value '${templateOptions.nationalityDefault}'.`,
      ]);
      instructionSheet.getColumn(1).width = 120;

      templateSheet.addRow(templateOptions.headers);
      templateSheet.views = [{state: "frozen", ySplit: 1}];
      templateSheet.autoFilter = {
        from: "A1",
        to: `${lastColumnLetter}1`,
      };
      [
        18, 18, 22, 12, 18, 22, 18, 18, 18, 18, 18, 18, 24, 28, 20, 14, 24, 20,
        20, 18, 14, 18,
      ].forEach((width, index) => {
        if (index >= templateOptions.headers.length) {
          return;
        }
        templateSheet.getColumn(index + 1).width = width;
      });
      templateSheet.getColumn(columnMap["National ID (FAN)"]).numFmt = "@";

      const lookupGroups = [
        {header: "Sex", values: templateOptions.sexOptions},
        {header: "Ethnicity", values: templateOptions.ethnicityOptions},
        {header: "Birth Region", values: templateOptions.birthRegionOptions},
        {header: "Birth Zone", values: templateOptions.birthZoneOptions},
        {header: "Birth Woreda", values: templateOptions.birthWoredaOptions},
        {
          header: "Program Education Type",
          values: templateOptions.programOptions,
        },
        {
          header: "Department / Discipline",
          values: templateOptions.disciplineOptions,
        },
        {
          header: "Level Of Study",
          values: templateOptions.levelOfStudyOptions,
        },
        {
          header: "Year of Education",
          values: templateOptions.yearOfEducationOptions,
        },
        {
          header: "Student Status",
          values: templateOptions.studentStatusOptions,
        },
      ];

      lookupSheet.addRow(lookupGroups.map((group) => group.header));
      const lookupHeight = Math.max(
        ...lookupGroups.map((group) => group.values.length),
      );
      for (let rowIndex = 0; rowIndex < lookupHeight; rowIndex += 1) {
        lookupSheet.addRow(
          lookupGroups.map((group) => group.values[rowIndex] || ""),
        );
      }

      const listFormulaByHeader = lookupGroups.reduce(
        (accumulator, group, index) => {
          const columnLetter = this.getColumnLetter(index + 1);
          accumulator[group.header] =
            `Lookups!$${columnLetter}$2:$${columnLetter}$${group.values.length + 1}`;
          return accumulator;
        },
        {},
      );

      for (let rowNumber = 2; rowNumber <= 201; rowNumber += 1) {
        templateSheet.getCell(
          `${columnMap["Sex"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Sex"]],
          showErrorMessage: true,
          errorTitle: "Invalid Sex",
          error: "Select Male or Female from the dropdown.",
        };
        templateSheet.getCell(
          `${columnMap["Date of Birth"]}${rowNumber}`,
        ).dataValidation = {
          type: "date",
          operator: "lessThanOrEqual",
          allowBlank: false,
          formulae: [new Date(`${maxBirthDate}T00:00:00`)],
          showErrorMessage: true,
          errorTitle: "Invalid Date of Birth",
          error: `Date of Birth must be on or before ${maxBirthDate}.`,
        };
        templateSheet.getCell(
          `${columnMap["National ID (FAN)"]}${rowNumber}`,
        ).numFmt = "@";
        templateSheet.getCell(
          `${columnMap["National ID (FAN)"]}${rowNumber}`,
        ).dataValidation = {
          type: "textLength",
          operator: "equal",
          allowBlank: false,
          formulae: [16],
          showErrorMessage: true,
          errorTitle: "Invalid National ID",
          error:
            "National ID (FAN) must be exactly 16 digits and should be entered in a Text cell.",
        };
        templateSheet.getCell(
          `${columnMap["Ethnicity"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Ethnicity"]],
          showErrorMessage: true,
          errorTitle: "Invalid Ethnicity",
          error: "Select an ethnicity from the dropdown list.",
        };
        templateSheet.getCell(
          `${columnMap["Birth Region"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Birth Region"]],
          showErrorMessage: true,
          errorTitle: "Invalid Birth Region",
          error: "Select a birth region from the dropdown list.",
        };
        templateSheet.getCell(
          `${columnMap["Birth Zone"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Birth Zone"]],
          showErrorMessage: true,
          errorTitle: "Invalid Birth Zone",
          error: "Select a birth zone from the dropdown list.",
        };
        templateSheet.getCell(
          `${columnMap["Birth Woreda"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Birth Woreda"]],
          showErrorMessage: true,
          errorTitle: "Invalid Birth Woreda",
          error: "Select a birth woreda from the dropdown list.",
        };
        templateSheet.getCell(
          `${columnMap["Program Education Type"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Program Education Type"]],
          showErrorMessage: true,
          errorTitle: "Invalid Program Education Type",
          error: `Select one of: ${formatAllowedValues(templateOptions.programOptions)}.`,
        };
        templateSheet.getCell(
          `${columnMap["Department / Discipline"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Department / Discipline"]],
          showErrorMessage: true,
          errorTitle: "Invalid Discipline",
          error: "Select a discipline from the dropdown list.",
        };
        templateSheet.getCell(
          `${columnMap["Level Of Study"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Level Of Study"]],
          showErrorMessage: true,
          errorTitle: "Invalid Level Of Study",
          error: `Select one of: ${formatAllowedValues(templateOptions.levelOfStudyOptions)}.`,
        };
        templateSheet.getCell(
          `${columnMap["Year of Entry"]}${rowNumber}`,
        ).dataValidation = {
          type: "whole",
          operator: "between",
          allowBlank: false,
          formulae: [1950, currentYear],
          showErrorMessage: true,
          errorTitle: "Invalid Entry Year",
          error: `Year of Entry must be between 1950 and ${currentYear}.`,
        };
        templateSheet.getCell(
          `${columnMap["Expected Graduation Year"]}${rowNumber}`,
        ).dataValidation = {
          type: "whole",
          operator: "between",
          allowBlank: false,
          formulae: [1950, 9999],
          showErrorMessage: true,
          errorTitle: "Invalid Expected Graduation Year",
          error: "Expected Graduation Year must be a valid 4-digit year.",
        };
        templateSheet.getCell(
          `${columnMap["Year of Graduation"]}${rowNumber}`,
        ).dataValidation = {
          type: "whole",
          operator: "between",
          allowBlank: true,
          formulae: [1950, 9999],
          showErrorMessage: true,
          errorTitle: "Invalid Graduation Year",
          error: "Year of Graduation must be a valid 4-digit year.",
        };
        templateSheet.getCell(
          `${columnMap["Year of Education"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Year of Education"]],
          showErrorMessage: true,
          errorTitle: "Invalid Year of Education",
          error: `Select one of: ${formatAllowedValues(templateOptions.yearOfEducationOptions)}.`,
        };
        templateSheet.getCell(
          `${columnMap["Student Status"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Student Status"]],
          showErrorMessage: true,
          errorTitle: "Invalid Student Status",
          error: `Select one of: ${formatAllowedValues(templateOptions.studentStatusOptions)}.`,
        };
        templateSheet.getCell(`${columnMap["GPA"]}${rowNumber}`).dataValidation =
          {
            type: "decimal",
            operator: "between",
            allowBlank: false,
            formulae: [0, 4],
            showErrorMessage: true,
            errorTitle: "Invalid GPA",
            error: "GPA must be between 0 and 4.",
          };
        templateSheet.getCell(
          `${columnMap["Cumulative GPA"]}${rowNumber}`,
        ).dataValidation = {
          type: "decimal",
          operator: "between",
          allowBlank: false,
          formulae: [0, 4],
          showErrorMessage: true,
          errorTitle: "Invalid Cumulative GPA",
          error: "Cumulative GPA must be between 0 and 4.",
        };
      }

      const headerRow = templateSheet.getRow(1);
      headerRow.font = {bold: true};
      headerRow.alignment = {vertical: "middle", horizontal: "center"};

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = "Generic_Entry_Template.xlsx";
      saveAs(blob, fileName);
      this.$toast.success(`${fileName} downloaded.`);
    },
    clearFilters() {
      this.name = null;
      this.idValue = null;
      this.programValue = null;
      this.disciplineValue = null;
      this.levelOfStudyValue = null;
      this.DISCIPLINES = this.allDisciplines;
      this.options.page = 1;
      this.fetchImportedGraduates();
    },
    async fetchOnFilters() {
      if (!this.programValue?.id) {
        this.DISCIPLINES = this.allDisciplines;
        return;
      }
      this.DISCIPLINES = this.allDisciplines.filter(x => x.educationProgramId == this.programValue.id);
    },
    async loadLookups() {
      this.lookupLoading = true;
      try {
        this.EDUC_LEVEL = await EducLevelEnum;
        this.LEVEL_OF_STUDIES = await getLevelOfStudies();
        this.LEVEL_OF_STUDIES = this.LEVEL_OF_STUDIES.rows.filter(x => x?.level_type == 'Generic Entry');
        this.allDisciplines = await getDisciplines({
          scope: "standard",
          levelType: "Generic Entry",
        });
        this.allDisciplines = this.allDisciplines.rows;
        this.DISCIPLINES = this.allDisciplines;
        this.EDUCATIONAL_PROGRAMS = await getEducationalPrograms({scope: "standard"});
        this.EDUCATIONAL_PROGRAMS = this.EDUCATIONAL_PROGRAMS.rows;
        this.STUDENT_STATUSES = await getStudentStatuses();
        this.YEAR_OF_EDUCATION = [{name: "Not Applicable", code: "NA"}].concat(await YearOfEducEnum);
        this.ETHNICITIES = (await getEthnicities())?.rows || [];
        this.BIRTH_REGIONS = ((await getRegionList()) || []).filter((item) => item?.isActive !== false);
        this.BIRTH_ZONES = ((await getZoneList()) || []).filter((item) => item?.isActive !== false);
        this.BIRTH_WOREDAS = ((await getWoredaList()) || []).filter((item) => item?.isActive !== false);
        const universityResponse = await getUniversities();
        const activeUniversities = (universityResponse?.rows || []).filter(
          (item) => item?.isActive !== false,
        );
        const assignedUniversity = getAssignedUniversityOption();
        this.UNIVERSITIES =
          isUniversityScopedPreserviceUser() && assignedUniversity
            ? [assignedUniversity]
            : activeUniversities;
      } catch (error) {
        console.error(error);
        this.$toast.error("Unable to load the Generic Entry form lookups. Please retry.");
      } finally {
        this.lookupLoading = false;
      }
    },
    beforeUpload(file) {
      const isLt1M = file.size / 1024 / 1024 < 1;
      if (isLt1M) {
        return true;
      }
      this.$toast.warning("Please do not upload files larger than 1m in size.");
      return false;
    },
    newImport() {
      this.showTable = false;
    },
    goBack() {
      this.showTable = true;
      this.showUpload = false;
      this.tableData = [];
      this.fileName = "";
      this.importErrors = [];
      this.importErrorsDialog = false;
      this.fetchImportedGraduates();
    },
    replaceImportFile() {
      this.showUpload = false;
      this.tableData = [];
      this.fileName = "";
      this.importErrors = [];
      this.importErrorsDialog = false;
    },
    closeDialog() {
      this.dialog = false;
      this.detailDialog = false;
      this.addDialog = false;
      this.fetchImportedGraduates();
    },
    handleSuccess({header, results, fileName, sheets}) {
      const sourceValidation = validatePreserviceImportSource({
        fileName,
        headers: header,
        sheets,
        expectedHeaders: GENERIC_ENTRY_TEMPLATE_HEADERS,
        expectedLegacyHeaders: LEGACY_GENERIC_ENTRY_TEMPLATE_HEADERS,
        expectedTemplateSheetName: "Generic Entry Template",
      });
      if (sourceValidation.hasErrors) {
        this.importErrors = sourceValidation.errors;
        this.importErrorsDialog = true;
        return;
      }

      const validation = validateGenericEntryImport({
        rows: results,
        statusRows: sourceValidation.usesSeparateStatusSheet
          ? sheets?.[PRESERVICE_STATUS_SHEET_NAME]?.results || []
          : null,
        lookups: {
          disciplines: this.allDisciplines,
          educationalPrograms: this.EDUCATIONAL_PROGRAMS,
          ethnicities: this.ETHNICITIES,
          birthRegions: this.BIRTH_REGIONS,
          birthZones: this.BIRTH_ZONES,
          birthWoredas: this.BIRTH_WOREDAS,
          levelOfStudies: this.LEVEL_OF_STUDIES,
          studentStatuses: this.STUDENT_STATUSES,
          yearOfEducation: this.YEAR_OF_EDUCATION,
          universities: this.UNIVERSITIES,
        },
      });

      if (validation.hasErrors) {
        this.importErrors = validation.errors;
        this.importErrorsDialog = true;
        return;
      }
      this.importErrorsDialog = false;
      this.showUpload = true;
      this.tableData = validation.validRows;
      this.fileName = fileName;
      this.$toast.success(`${validation.validRows.length} rows are ready to import.`);
    },
    async uploadGraduates() {
      if (this.tableData.length === 0) {
        this.$toast.error("No file detected, Please provide one!");
        return;
      }
      this.loading = true;
      try {
        let res = await GraduateService.importGenericEntry(this.tableData);
        if (res.status === 200) {
          this.showUpload = false;
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fileName = "";
              this.fetchImportedGraduates();
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.showUpload = false;
          this.fetchImportedGraduates();
          this.$toast.error(
              "There was a problem uploading file, Please try Again!"
          );
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchImportedGraduates() {
      this.loading = true;
      const {page, itemsPerPage} = this.options;

      let searchParams = '&preServiceLevel=GenericEntry';
      this.name !== null && (searchParams += '&name=' + this.name);
      this.idValue !== null && (searchParams += '&idNo=' + normalizeFanId(this.idValue));
      this.programValue !== null && (searchParams += '&educationProgramId=' + this.programValue.id);
      this.disciplineValue !== null && (searchParams += '&disciplineId=' + this.disciplineValue.id);
      this.levelOfStudyValue !== null && (searchParams += '&levelOfStudyId=' + this.levelOfStudyValue.id);


      let res = await GraduateService.getPostServices(page, itemsPerPage, searchParams);
      if (res.status === 200) {
        this.loading = false;
        this.showTable = true;
        this.tableData = res.data;
      }
    },
    viewImportedGraduates(item) {
      this.graduates = item;
      this.detailDialog = true;
      this.title = "Generic Entries Detail";
    },
    editImportedGraduate(item) {
      this.addDialog = true;
      this.title = "Edit Generic Entry";
      this.graduates = item;
    },
    async updateImportedGraduate(graduates) {
      this.loading = true;
      let res = null;
      res = await GraduateService.updatePreService(
          graduates.id,
          graduates
      );

      if (res.status === 201) {
        switch (res.data.status) {
          case 200:
            this.$toast.success(res.data.message);
            this.fetchImportedGraduates();
            break;
          case 500:
            this.$toast.error(res.data.message);
            this.loading = false;
            break;
        }
      } else {
        this.$toast.error("Unable to Update pre-service, please try again");
      }
    },
    addGenericEntry() {
      this.graduates = null;
      this.title = "Add Generic Entry";
      this.addDialog = true;
    },
    async registerOrUpdateGenericEntries(graduates) {
      if (this.saving) return;
      this.saving = true;

      try {
        const res = graduates.editMode
            ? await GraduateService.updatePreService(graduates.id, graduates)
            : await GraduateService.createPreService(graduates);

        if (res?.data?.status !== 200) {
          this.$toast.error(
              res?.data?.message ||
              `Unable to ${graduates.editMode ? "update" : "create"} generic entry, please try again`
          );
          return;
        }

        this.$toast.success(res.data.message);
        this.addDialog = false;
        this.fetchImportedGraduates().catch((error) => {
          console.error(error);
          this.loading = false;
          this.$toast.error(
              "The entry was saved, but the list could not be refreshed. Please reload the page."
          );
        });
      } catch (error) {
        this.$toast.error(
            error?.response?.data?.message ||
            `Unable to ${graduates.editMode ? "update" : "create"} generic entry, please try again`
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style scoped>
.selected {
  line-height: 16px;
  margin: 0 auto;
  font-size: 24px;
  border-radius: 5px;
  text-align: center;
  color: #bbb;
  position: relative;
}

.preservice-list-card {
  border-radius: 24px;
}

.filter-toolbar {
  margin-bottom: 16px;
  padding: 20px;
  border: 1px solid #dbe7f3;
  border-radius: 24px;
  background: linear-gradient(135deg, #f7fbff 0%, #eef6ff 52%, #ffffff 100%);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.filter-toolbar__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.filter-toolbar__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #2563eb;
}

.filter-toolbar__title {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.15;
  color: #0f172a;
}

.filter-toolbar__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-toolbar__fields {
  margin: 0 !important;
}

.filter-toolbar__field {
  background: rgba(255, 255, 255, 0.9);
}

.filter-toolbar__primary-button,
.filter-toolbar__secondary-button,
.filter-toolbar__clear-button {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.import-entry-shell {
  margin: 0 8px 24px;
}

.import-entry-card {
  padding: 20px;
  border: 1px solid #dbe7f3;
  border-radius: 24px;
  background: linear-gradient(145deg, #f8fbff 0%, #eef5ff 48%, #ffffff 100%);
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.08);
}

.import-entry-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.import-entry-card__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #2563eb;
}

.import-entry-card__title {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.import-entry-card__subtitle {
  margin-top: 8px;
  max-width: 640px;
  color: #475569;
}

.import-entry-card__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.import-entry-card__steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.import-entry-card__step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  color: #334155;
}

.import-entry-card__step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-weight: 700;
}

.import-entry-card__uploader {
  margin-top: 20px;
}
</style>
